import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { attachments as api } from '@/api/endpoints'
import type { AttachmentResponse } from '@/api/types'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { cx } from '@/lib/cx'
import { useScrollLock } from '@/lib/useScrollLock'

interface Props {
  attachments: AttachmentResponse[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

const MAX_ZOOM = 6
const MIN_ZOOM = 1

/** Every control on the bar. Close is the odd one out and says so in colour. */
const BAR_BUTTON =
  'grid size-touch flex-none place-items-center rounded-md border-0 bg-white/15 text-on-overlay transition-colors duration-150 ease-standard hover:not-disabled:bg-white/25 disabled:opacity-40'

/**
 * Full screen at every size — an X-ray is examined, not glanced at.
 *
 * Nothing here downloads on its own. A report that saves itself the moment it
 * is opened has left the application's access control and audit trail, and the
 * doctor only wanted to look at it; saving is the button that says so.
 */
export function AttachmentViewer({ attachments, index, onIndexChange, onClose }: Props) {
  const attachment = attachments[index]
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const closeRef = useRef<HTMLButtonElement>(null)
  const previouslyFocused = useRef<Element | null>(null)
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const pinch = useRef<{ distance: number; zoom: number } | null>(null)

  // Inline, so the browser shows it rather than saving it.
  const { data, isLoading } = useQuery({
    queryKey: ['attachment-view', attachment?.id],
    queryFn: () => api.download(attachment!.id, 'inline'),
    enabled: Boolean(attachment),
    staleTime: 4 * 60_000,
  })

  useEffect(() => {
    previouslyFocused.current = document.activeElement
    closeRef.current?.focus()
    return () => {
      ;(previouslyFocused.current as HTMLElement | null)?.focus?.()
    }
  }, [])

  const reset = useCallback(() => {
    setZoom(1)
    setRotation(0)
    setPan({ x: 0, y: 0 })
  }, [])

  const go = useCallback(
    (delta: number) => {
      const next = index + delta
      if (next >= 0 && next < attachments.length) {
        onIndexChange(next)
        reset()
      }
    },
    [index, attachments.length, onIndexChange, reset],
  )

  const zoomBy = useCallback((factor: number) => {
    setZoom((z) => {
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor))
      // Back to the middle once it fits again, or it stays nudged off-centre
      // with nothing to drag back.
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 })
      return next
    })
  }, [])

  // The record behind stays where it was. This fills the screen, so a page
  // scrolling underneath is invisible until the X-ray is closed and the
  // history has moved — and on a phone the pinch that zooms the image is the
  // same gesture that would drag the page.
  useScrollLock(true)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') go(1)
      if (event.key === 'ArrowLeft') go(-1)
      if (event.key === '+' || event.key === '=') zoomBy(1.25)
      if (event.key === '-') zoomBy(1 / 1.25)
      if (event.key === '0') reset()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose, zoomBy, reset])

  /** Saving a copy is deliberate, so it asks for its own signed URL. */
  async function save() {
    if (!attachment) return
    const target = await api.download(attachment.id, 'attachment')
    window.location.href = target.download_url
  }

  if (!attachment) return null
  const isPdf = attachment.content_type === 'application/pdf'

  return (
    <div
      className="fixed inset-0 z-viewer flex h-dvh flex-col bg-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Attachment viewer"
    >
      <div className="flex flex-none items-center gap-2 px-3 pt-safe-3 pb-3 text-on-overlay">
        {/* Red, and first: the one control that leaves, told apart from the
            six that change what you are looking at. */}
        <button
          ref={closeRef}
          type="button"
          className={cx(BAR_BUTTON, 'bg-danger/90 hover:not-disabled:bg-danger')}
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" />
        </button>

        <span className="min-w-0 flex-1 truncate text-sm">
          {attachment.original_filename}
          {attachments.length > 1 && (
            <span className="ml-2 text-on-overlay/70 tabular-nums">
              {index + 1} of {attachments.length}
            </span>
          )}
        </span>

        {!isPdf && (
          <>
            <button
              type="button"
              className={BAR_BUTTON}
              onClick={() => setRotation((r) => (r + 90) % 360)}
              aria-label="Rotate 90 degrees"
            >
              <Icon name="rotate" />
            </button>
            <button
              type="button"
              className={BAR_BUTTON}
              onClick={() => zoomBy(1 / 1.25)}
              disabled={zoom <= MIN_ZOOM}
              aria-label="Zoom out"
            >
              <Icon name="zoomOut" />
            </button>
            <button
              type="button"
              className={BAR_BUTTON}
              onClick={() => zoomBy(1.25)}
              disabled={zoom >= MAX_ZOOM}
              aria-label="Zoom in"
            >
              <Icon name="zoomIn" />
            </button>
          </>
        )}

        <button type="button" className={BAR_BUTTON} onClick={() => void save()} aria-label="Download">
          <Icon name="save" />
        </button>

        {attachments.length > 1 && (
          <>
            <button
              type="button"
              className={BAR_BUTTON}
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label="Previous attachment"
            >
              <Icon name="chevronLeft" />
            </button>
            <button
              type="button"
              className={BAR_BUTTON}
              onClick={() => go(1)}
              disabled={index === attachments.length - 1}
              aria-label="Next attachment"
            >
              <Icon name="chevronRight" />
            </button>
          </>
        )}
      </div>

      {/* min-h-0 so this takes the rest of the screen and no more: without it
          a tall image pushes the bar off the top of a phone. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {isLoading || !data ? (
          <div className="grid h-full place-items-center">
            <Spinner label="Opening attachment" />
          </div>
        ) : isPdf ? (
          // Shown, not downloaded. The URL is signed `inline`, so the browser's
          // own paged reader renders it here.
          <iframe
            className="size-full border-0 bg-canvas"
            src={data.download_url}
            title={attachment.original_filename}
          />
        ) : (
          <div
            className={cx(
              'grid h-full touch-none place-items-center overflow-hidden p-2',
              zoom > MIN_ZOOM ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
            )}
            onDoubleClick={() => (zoom > MIN_ZOOM ? reset() : zoomBy(2))}
            onPointerDown={(event) => {
              if (zoom <= MIN_ZOOM) return
              ;(event.target as Element).setPointerCapture?.(event.pointerId)
              drag.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y }
            }}
            onPointerMove={(event) => {
              if (!drag.current) return
              setPan({
                x: drag.current.panX + (event.clientX - drag.current.x),
                y: drag.current.panY + (event.clientY - drag.current.y),
              })
            }}
            onPointerUp={() => {
              drag.current = null
            }}
            onPointerCancel={() => {
              drag.current = null
            }}
            // Pinch on a phone and the trackpad's own zoom gesture, which
            // arrives as a wheel event with ctrlKey set.
            onWheel={(event) => {
              if (!event.ctrlKey) return
              zoomBy(event.deltaY < 0 ? 1.1 : 1 / 1.1)
            }}
            onTouchStart={(event) => {
              if (event.touches.length !== 2) return
              const [a, b] = [event.touches[0]!, event.touches[1]!]
              pinch.current = {
                distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
                zoom,
              }
            }}
            onTouchMove={(event) => {
              if (event.touches.length !== 2 || !pinch.current) return
              const [a, b] = [event.touches[0]!, event.touches[1]!]
              const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
              const next = (pinch.current.zoom * distance) / pinch.current.distance
              setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next)))
            }}
            onTouchEnd={() => {
              pinch.current = null
            }}
          >
            <img
              className="max-h-full max-w-full select-none"
              draggable={false}
              src={data.download_url}
              alt={`${attachment.kind} — ${attachment.original_filename}`}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: drag.current ? 'none' : 'transform 150ms var(--ease-standard)',
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
